import { get, post } from './client';
import type {
  KodifConversationsOptions,
  KodifConversationsResponse,
  KodifAnalyticsParams,
  KodifKpiSummaryData,
  KodifVolumeData,
  KodifContainmentByChannelData,
  KodifChannelDistributionData,
  KodifCsatByContainmentData,
  KodifTopicsData,
  KodifHandoffAnalysisData,
  KodifConversationDepthData,
  KodifActionsData,
} from '@/types/kodif';

export interface KodifCredentials {
  api_key?: string;
  base_url?: string;
  clear_credentials?: boolean;
}

export interface KodifCredentialsResponse {
  kodif_configured: boolean;
  kodif_base_url: string;
}

export interface KodifTestResponse {
  status: string;
  connected: boolean;
  sample_count: number;
  next_page_uri?: string | null;
  base_url?: string;
}

export async function updateKodifCredentials(
  teamId: string,
  credentials: KodifCredentials
): Promise<KodifCredentialsResponse> {
  return post<KodifCredentialsResponse>(`/api/teams/${teamId}/kodif/credentials`, credentials);
}

export async function testKodifConnection(teamId: string): Promise<KodifTestResponse> {
  return get<KodifTestResponse>(`/api/teams/${teamId}/kodif/test`);
}

export async function listKodifConversations(
  teamId: string,
  options: KodifConversationsOptions
): Promise<KodifConversationsResponse> {
  const params = new URLSearchParams({
    start_date: options.start_date,
    end_date: options.end_date,
  });

  if (options.page !== undefined) params.set('page', String(options.page));
  if (options.page_size !== undefined) params.set('page_size', String(options.page_size));
  if (options.channel) params.set('channel', options.channel);

  return get<KodifConversationsResponse>(
    `/api/teams/${teamId}/kodif/conversations?${params.toString()}`
  );
}

// ── Analytics API functions ─────────────────────────────────────

function buildKodifAnalyticsParams(p: KodifAnalyticsParams): string {
  const params = new URLSearchParams({
    start_date: p.start_date,
    end_date: p.end_date,
  });
  if (p.channel && p.channel !== 'ALL') params.set('channel', p.channel);
  return params.toString();
}

export async function getKodifKpiSummary(teamId: string, p: KodifAnalyticsParams): Promise<KodifKpiSummaryData> {
  return get<KodifKpiSummaryData>(`/api/teams/${teamId}/kodif/analytics/kpi-summary?${buildKodifAnalyticsParams(p)}`);
}

export async function getKodifVolume(teamId: string, p: KodifAnalyticsParams): Promise<KodifVolumeData> {
  return get<KodifVolumeData>(`/api/teams/${teamId}/kodif/analytics/volume?${buildKodifAnalyticsParams(p)}`);
}

export async function getKodifContainmentByChannel(teamId: string, p: KodifAnalyticsParams): Promise<KodifContainmentByChannelData> {
  return get<KodifContainmentByChannelData>(`/api/teams/${teamId}/kodif/analytics/containment-by-channel?${buildKodifAnalyticsParams(p)}`);
}

export async function getKodifChannelDistribution(teamId: string, p: KodifAnalyticsParams): Promise<KodifChannelDistributionData> {
  return get<KodifChannelDistributionData>(`/api/teams/${teamId}/kodif/analytics/channel-distribution?${buildKodifAnalyticsParams(p)}`);
}

export async function getKodifCsatByContainment(teamId: string, p: KodifAnalyticsParams): Promise<KodifCsatByContainmentData> {
  return get<KodifCsatByContainmentData>(`/api/teams/${teamId}/kodif/analytics/csat-by-containment?${buildKodifAnalyticsParams(p)}`);
}

export async function getKodifTopics(teamId: string, p: KodifAnalyticsParams): Promise<KodifTopicsData> {
  return get<KodifTopicsData>(`/api/teams/${teamId}/kodif/analytics/topics?${buildKodifAnalyticsParams(p)}`);
}

export async function getKodifHandoffAnalysis(teamId: string, p: KodifAnalyticsParams): Promise<KodifHandoffAnalysisData> {
  return get<KodifHandoffAnalysisData>(`/api/teams/${teamId}/kodif/analytics/handoff-analysis?${buildKodifAnalyticsParams(p)}`);
}

export async function getKodifConversationDepth(teamId: string, p: KodifAnalyticsParams): Promise<KodifConversationDepthData> {
  return get<KodifConversationDepthData>(`/api/teams/${teamId}/kodif/analytics/conversation-depth?${buildKodifAnalyticsParams(p)}`);
}

export async function getKodifActions(teamId: string, p: KodifAnalyticsParams): Promise<KodifActionsData> {
  return get<KodifActionsData>(`/api/teams/${teamId}/kodif/analytics/actions?${buildKodifAnalyticsParams(p)}`);
}
